import {
  child,
  get,
  limitToLast,
  onValue,
  orderByChild,
  push,
  query,
  ref,
  remove,
  runTransaction,
  set,
  update,
} from 'firebase/database'

import { db } from './firebase.js'

// Custom billing days are constrained to 5–27 (avoids month-length edge
// cases). Returns an integer in range, or null if invalid / out of range.
export function clampCustomDay(v) {
  const n = Math.trunc(Number(v))
  if (!Number.isFinite(n) || n < 5 || n > 27) return null
  return n
}

// ── Customers ──────────────────────────────────────────────────────────────
export function watchCustomers(cb) {
  return onValue(ref(db, 'customers'), (snap) => cb(snap.val() || {}))
}

export function watchCustomer(id, cb) {
  return onValue(ref(db, `customers/${id}`), (snap) => cb(snap.val()))
}

export async function createCustomer(payload) {
  const node = push(ref(db, 'customers'))
  const customerId = node.key
  // paymentMethod is the canonical field going forward. autoDraft is
  // kept in sync with the Stripe semantics for backwards compatibility
  // (legacy code paths in functions/index.js + the email template still
  // read autoDraft).
  const paymentMethod = ['stripe', 'stripe_invoice', 'card_auto', 'card_invoice', 'manual'].includes(payload.paymentMethod)
    ? payload.paymentMethod
    : (payload.autoDraft === false ? 'manual' : 'stripe')
  // The two auto-charge rails (bank auto-draft + card auto-pay) flip the
  // legacy autoDraft flag on so the scheduler picks them up. *_invoice
  // rails are pay-on-click: customer taps Pay on the emailed invoice.
  const autoDraft = paymentMethod === 'stripe' || paymentMethod === 'card_auto'
  const record = {
    name: (payload.name || '').trim(),
    email: (payload.email || '').trim(),
    phone: (payload.phone || '').trim(),
    address: (payload.address || '').trim(),
    servicePlan: (payload.servicePlan || '').trim(),
    monthlyAmount: Number(payload.monthlyAmount) || 0,
    // Additive: '15th' and 'custom' pass through; anything else → '1st'
    // (so the existing '1st'/'15th' outcomes are unchanged).
    billingCycle: ['15th', 'custom'].includes(payload.billingCycle) ? payload.billingCycle : '1st',
    customSendDay: payload.billingCycle === 'custom' ? clampCustomDay(payload.customSendDay) : null,
    customDueDay: payload.billingCycle === 'custom' ? clampCustomDay(payload.customDueDay) : null,
    paymentMethod,
    autoDraft,
    status: 'active',
    notes: (payload.notes || '').trim(),
    createdAt: Date.now(),
    stripeCustomerId: payload.stripeCustomerId || null,
    stripePaymentMethodId: payload.stripePaymentMethodId || null,
  }
  await set(node, record)
  return customerId
}

// Derive the customer's payment method. The `paymentMethod` field is
// the source of truth when present; older records fall back to a
// Stripe/manual inference based on the legacy autoDraft flag.
export function paymentMethodOf(customer) {
  if (!customer) return 'stripe'
  if (customer.paymentMethod === 'mail')           return 'mail'
  if (customer.paymentMethod === 'manual')         return 'manual'
  if (customer.paymentMethod === 'card_auto')      return 'card_auto'
  if (customer.paymentMethod === 'card_invoice')   return 'card_invoice'
  if (customer.paymentMethod === 'stripe_invoice') return 'stripe_invoice'
  if (customer.paymentMethod === 'stripe')         return 'stripe'
  return customer.autoDraft === false ? 'manual' : 'stripe'
}

// A customer is "paid or set up" if they have a saved payment method on file
// OR their current (most-recent) invoice is already paid. This makes a one-time
// card payment — which saves NO payment method — still read as paid instead of
// "setup incomplete". `currentInvoice` is the customer's most recent invoice.
export function isPaidOrSetUp(customer, currentInvoice) {
  return !!customer?.stripePaymentMethodId || currentInvoice?.status === 'paid'
}

// Names are stored as a single "First Last" string, so a last-name sort takes
// the LAST token as the surname (suffixes Jr/Sr/II–IV dropped). Business/edge
// records can carry an explicit `sortName` override (e.g. "Tim Miller Wrecking"
// → "miller" so it sits by "Tim Miller").
const NAME_SUFFIX = new Set(['ii', 'iii', 'iv', 'jr', 'sr'])
export function lastNameKey(name) {
  const toks = String(name || '')
    .replace(/[.,'’]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((t) => !NAME_SUFFIX.has(t.toLowerCase()))
  return (toks[toks.length - 1] || '').toLowerCase()
}

// Comparator: sort customers by last name (honoring `sortName`), tie-breaking on
// the full name so shared-surname people keep a sensible first-name order.
export function byLastName(a, b) {
  const ka = (a?.sortName || lastNameKey(a?.name)).toLowerCase()
  const kb = (b?.sortName || lastNameKey(b?.name)).toLowerCase()
  return ka.localeCompare(kb) || (a?.name || '').localeCompare(b?.name || '')
}

export async function updateCustomer(id, patch) {
  await update(ref(db, `customers/${id}`), patch)
}

export async function deleteCustomer(id) {
  await remove(ref(db, `customers/${id}`))
}

export async function setCustomerStatus(id, status) {
  await update(ref(db, `customers/${id}`), { status })
}

// ── Invoices ───────────────────────────────────────────────────────────────
export function watchInvoices(cb) {
  return onValue(ref(db, 'invoices'), (snap) => cb(snap.val() || {}))
}

export function watchCustomerInvoices(customerId, cb) {
  return onValue(ref(db, 'invoices'), (snap) => {
    const all = snap.val() || {}
    const out = {}
    for (const [k, v] of Object.entries(all)) {
      if (v?.customerId === customerId) out[k] = v
    }
    cb(out)
  })
}

// ── Invoice-number reservation — SINGLE SOURCE OF TRUTH ───────────────────
// settings/nextInvoiceNumber holds the NEXT invoice number to assign — the
// SAME definition the cloud functions use (functions/index.js), so the two
// paths can never disagree. Atomically consumes the next number and returns
// the value assigned (committed - 1).
async function nextInvoiceNumber() {
  const numRef = ref(db, 'settings/nextInvoiceNumber')
  const result = await runTransaction(numRef, (cur) => {
    const n = Number.isFinite(Number(cur)) ? Number(cur) : 1000
    return n + 1
  })
  const committed = Number(result?.snapshot?.val()) || 1001
  return committed - 1
}

// Highest invoiceNumber on any existing invoice (0 if none).
export async function maxExistingInvoiceNumber() {
  const all = (await get(ref(db, 'invoices'))).val() || {}
  let max = 0
  for (const inv of Object.values(all)) {
    const n = Number(inv?.invoiceNumber)
    if (Number.isFinite(n) && n > max) max = n
  }
  return max
}

// Duplicate guard — mirrors functions/index.js reserveUniqueInvoiceNumber().
// Reserve a number guaranteed not to collide with an existing invoice; if the
// reserved number is already used (counter drifted), jump to max(existing)+1
// and push the counter past it.
async function reserveUniqueInvoiceNumber() {
  const n = await nextInvoiceNumber()
  const all = (await get(ref(db, 'invoices'))).val() || {}
  let max = 0
  const taken = new Set()
  for (const inv of Object.values(all)) {
    const k = Number(inv?.invoiceNumber)
    if (Number.isFinite(k)) { taken.add(k); if (k > max) max = k }
  }
  if (!taken.has(n) && n > max) return n
  const fixed = max + 1
  await runTransaction(ref(db, 'settings/nextInvoiceNumber'), (cur) => {
    const c = Number(cur) || 0
    return c > fixed ? c : fixed + 1
  })
  return fixed
}

// Billed amount for a generated invoice — mirrors the server's
// invoiceAmountFor(). Debit-card customers carry a flat $3 card fee baked into
// the invoice total (no breakdown). Starts on the second invoice automatically
// (cardFunding:'debit' is only stamped after the first invoice). Credit cards
// are untouched here — their percentage surcharge is applied at charge time.
export const DEBIT_CARD_FEE = 3
export function invoiceAmountFor(c) {
  const base = Number(c?.monthlyAmount) || 0
  return c?.cardFunding === 'debit' ? base + DEBIT_CARD_FEE : base
}

export async function createInvoice({
  customerId,
  customerName,
  amount,
  dueDate,
  billingPeriod,
  status = 'pending',
}) {
  const invoiceNumber = await reserveUniqueInvoiceNumber()
  const node = push(ref(db, 'invoices'))
  const record = {
    invoiceNumber,
    customerId,
    customerName: customerName || '',
    amount: Number(amount) || 0,
    dueDate: dueDate || '',
    billingPeriod: billingPeriod || '',
    status,
    stripePaymentIntentId: null,
    paidAt: null,
    failReason: null,
    createdAt: Date.now(),
  }
  await set(node, record)
  return { invoiceId: node.key, invoiceNumber }
}

export async function setInvoiceStatus(invoiceId, status, extras = {}) {
  await update(ref(db, `invoices/${invoiceId}`), { status, ...extras })
}

export async function markInvoicePaid(invoiceId, opts = {}) {
  await update(ref(db, `invoices/${invoiceId}`), {
    status: 'paid',
    paidAt: Date.now(),
    ...opts,
  })
}

// ── Settings ───────────────────────────────────────────────────────────────
export function watchSettings(cb) {
  return onValue(ref(db, 'settings'), (snap) => cb(snap.val() || {}))
}

export async function updateSettings(patch) {
  await update(ref(db, 'settings'), patch)
}

// ── Billing helpers ────────────────────────────────────────────────────────
// Returns the YYYY-MM-DD due date for the next billing cycle of `kind`
// based on `now`. '1st' cycle → upcoming 1st of month; '15th' → upcoming 15th.
export function nextDueDate(kind, now = new Date()) {
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  const day = kind === '15th' ? 15 : 1
  // Step to current month's day; if it's already past, roll to next month.
  const candidate = new Date(d.getFullYear(), d.getMonth(), day)
  if (candidate < d) candidate.setMonth(candidate.getMonth() + 1)
  return candidate.toISOString().slice(0, 10)
}

export function billingPeriodLabel(date) {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric' })
}

// Run a billing cycle: generate one invoice per active customer on this
// cycle. Returns { invoicesCreated, totalAmount, customerIds }.
export async function runBillingCycle(kind) {
  const snap = await get(child(ref(db), 'customers'))
  const all = snap.val() || {}
  const eligible = Object.entries(all)
    .map(([id, c]) => ({ id, ...c }))
    .filter((c) => c.status === 'active' && c.billingCycle === kind && Number(c.monthlyAmount) > 0)
  const dueDate = nextDueDate(kind)
  const period = billingPeriodLabel(dueDate)
  const created = []
  let total = 0
  for (const c of eligible) {
    // Invoice status decides whether the scheduler auto-charges (pending)
    // or waits for the customer to click Pay (sent). Auto-charge requires
    // a saved Stripe payment method on the appropriate rail.
    const method = paymentMethodOf(c)
    const isAutoPay =
      (method === 'stripe' && c.autoDraft && c.stripePaymentMethodId) ||
      (method === 'card_auto' && c.stripePaymentMethodId)
    const billed = invoiceAmountFor(c)
    const { invoiceId, invoiceNumber } = await createInvoice({
      customerId: c.id,
      customerName: c.name,
      amount: billed,
      dueDate,
      billingPeriod: period,
      status: isAutoPay ? 'pending' : 'sent',
    })
    created.push({
      invoiceId, invoiceNumber, customerId: c.id,
      amount: billed,
      autoDraft: !!c.autoDraft,
      paymentMethod: method,
    })
    total += billed
  }
  // Persist a billing-run audit record.
  const runNode = push(ref(db, 'billingRuns'))
  await set(runNode, {
    kind,
    dueDate,
    period,
    invoicesCreated: created.length,
    totalAmount: total,
    runAt: Date.now(),
  })
  return { invoicesCreated: created.length, totalAmount: total, dueDate, period, created }
}

// Preview without writing — what would runBillingCycle('1st') produce?
export async function previewBillingCycle(kind) {
  const snap = await get(child(ref(db), 'customers'))
  const all = snap.val() || {}
  const eligible = Object.entries(all)
    .map(([id, c]) => ({ id, ...c }))
    .filter((c) => c.status === 'active' && c.billingCycle === kind && Number(c.monthlyAmount) > 0)
  const total = eligible.reduce((s, c) => s + (Number(c.monthlyAmount) || 0), 0)
  return {
    count: eligible.length,
    total,
    customers: eligible,
    dueDate: nextDueDate(kind),
    period: billingPeriodLabel(nextDueDate(kind)),
  }
}

export function watchBillingRuns(cb) {
  return onValue(ref(db, 'billingRuns'), (snap) => cb(snap.val() || {}))
}

// ── Email activity log ─────────────────────────────────────────────────────
// /emailLog is written by Cloud Functions on every Resend call. We pull
// the most-recent N entries (indexed on sentAt in database.rules.json)
// and hand the caller an already-sorted, newest-first array.
export function watchEmailLog(cb, limit = 50) {
  const q = query(ref(db, 'emailLog'), orderByChild('sentAt'), limitToLast(limit))
  return onValue(q, (snap) => {
    const out = []
    snap.forEach((child) => { out.push({ id: child.key, ...child.val() }) })
    out.sort((a, b) => (b.sentAt || 0) - (a.sentAt || 0))
    cb(out)
  })
}

// SMS activity log. Same indexed-tail pattern as watchEmailLog — used by
// the Dashboard "Texts sent this month" counter and the CustomerDetail
// "Last SMS sent" line.
export function watchSmsLog(cb, limit = 50) {
  const q = query(ref(db, 'smsLog'), orderByChild('sentAt'), limitToLast(limit))
  return onValue(q, (snap) => {
    const out = []
    snap.forEach((child) => { out.push({ id: child.key, ...child.val() }) })
    out.sort((a, b) => (b.sentAt || 0) - (a.sentAt || 0))
    cb(out)
  })
}

// ── Derived metrics for the Dashboard ──────────────────────────────────────
export function computeMetrics(customers, invoices) {
  const customerList = Object.values(customers || {})
  const invoiceList = Object.values(invoices || {})
  const todayStr = new Date().toISOString().slice(0, 10)

  const activeCustomers = customerList.filter((c) => c.status === 'active').length
  const monthlyRevenue = customerList
    .filter((c) => c.status === 'active')
    .reduce((s, c) => s + (Number(c.monthlyAmount) || 0), 0)

  let dueThisCycleCount = 0
  let dueThisCycleAmount = 0
  let overdueCount = 0
  let overdueAmount = 0
  let failedCount = 0

  for (const inv of invoiceList) {
    const isOpen = inv.status === 'pending' || inv.status === 'sent'
    if (isOpen) {
      dueThisCycleCount += 1
      dueThisCycleAmount += Number(inv.amount) || 0
      if (inv.dueDate && inv.dueDate < todayStr) {
        overdueCount += 1
        overdueAmount += Number(inv.amount) || 0
      }
    }
    if (inv.status === 'failed') failedCount += 1
    if (inv.status === 'overdue') {
      overdueCount += 1
      overdueAmount += Number(inv.amount) || 0
    }
  }

  return {
    activeCustomers,
    monthlyRevenue,
    dueThisCycleCount,
    dueThisCycleAmount,
    overdueCount,
    overdueAmount,
    failedCount,
  }
}

// Failed Payments tab: auto-draft invoices whose Stripe charge has been
// flagged after the retry cascade exhausted (attempts >= 2 + scheduler
// marks `flaggedFailed`). Excludes anything admin already resolved.
export function computeFailedPayments(invoices, customers) {
  const out = []
  for (const [id, inv] of Object.entries(invoices || {})) {
    if (!inv || !inv.flaggedFailed || inv.resolvedAt) continue
    if (inv.status === 'paid') continue
    out.push({ id, ...inv, customer: (customers || {})[inv.customerId] || null })
  }
  return out.sort((a, b) => (a.firstFailedAt || 0) - (b.firstFailedAt || 0))
}

// Overdue tab: manual/invoice customers (no autoDraft) with an unpaid
// invoice past its due date. Auto-draft customers go through the failed
// payments cascade instead.
export function computeOverdueInvoices(invoices, customers) {
  const todayStr = new Date().toISOString().slice(0, 10)
  const out = []
  for (const [id, inv] of Object.entries(invoices || {})) {
    // Never overdue: paid, voided, cancelled, refunded, OR a payment already in
    // flight (processing, or pending with a Stripe PaymentIntent attached) — an
    // in-flight/settling charge must not show as overdue.
    if (!inv) continue
    const st = inv.status
    if (st === 'paid' || st === 'voided' || st === 'cancelled' || st === 'refunded' || st === 'processing') continue
    if (st === 'pending' && inv.stripePaymentIntentId) continue
    const customer = (customers || {})[inv.customerId]
    if (!customer) continue
    if (customer.autoDraft) continue
    // Mail/check customers pay by check on their own schedule — never shown as overdue.
    if (customer.paymentMethod === 'mail') continue
    // Not-set-up customers (no saved payment method) are PENDING SETUP, not
    // overdue — they never completed payment setup. Same "set up" signal used
    // across the app: a saved Stripe PM OR a GoCardless mandate.
    if (!customer.stripePaymentMethodId && !customer.gocardlessMandateId) continue
    if (!inv.dueDate || inv.dueDate >= todayStr) continue
    out.push({ id, ...inv, customer })
  }
  return out.sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''))
}

export async function resolveFailedInvoice(invoiceId) {
  await update(ref(db, `invoices/${invoiceId}`), {
    flaggedFailed: false,
    resolvedAt: Date.now(),
    status: 'paid',
    paidAt: Date.now(),
    manualResolution: true,
  })
}

// Recent activity feed: last N invoices + status transitions.
export function computeRecentActivity(invoices, limit = 12) {
  return Object.entries(invoices || {})
    .map(([id, v]) => ({ id, ...v }))
    .sort((a, b) => (b.paidAt || b.createdAt || 0) - (a.paidAt || a.createdAt || 0))
    .slice(0, limit)
}

